/**
 * Verzoeklus-detectie in de browser.
 *
 * AANLEIDING — een lus die ik zelf veroorzaakte (07-08-2026). Een netwerkaanroep in
 * het mount-effect van `useAuth`, dat door 81 componenten wordt gebruikt, leidde tot
 * ~360 verzoeken per minuut uit één tabblad. Drie minuten lang. Geen enkele test ving
 * het, Sentry zag niets (het waren geslaagde 200's), en de app voelde normaal aan.
 * Alleen de Supabase-logs lieten het ritme zien — en dat is een plek waar je moet
 * gaan kijken.
 *
 * WAAROM AAN DE BRON EN NIET AAN DE GATEWAY. Gatewayverkeer is alleen zichtbaar via
 * Supabase' analytics-logs, en die zijn uitsluitend bereikbaar met een account-brede
 * token. Aan de clientkant kost dit geen enkel secret, en het is bovendien de plek
 * waar zo'n lus ontstáát.
 *
 * DE MOEILIJKHEID IS NIET TELLEN MAAR ONDERSCHEIDEN. Een dossierpagina vuurt makkelijk
 * twintig verzoeken tegelijk af; dat is gezond. Een lus is niet "veel verzoeken" maar
 * "hetzelfde verzoek, eindeloos". Daarom telt deze guard per GENORMALISEERD endpoint
 * en niet op totaalvolume.
 *
 * DE DREMPEL IS GEMETEN, NIET GEGOKT. De applicatie kent exact twee pollende queries
 * (`PlatformSupportGrantsPage`, `SupportConsolePage`), beide op 60_000 ms — dus
 * hoogstens 1 verzoek per minuut per endpoint. De drempel van 60 per minuut ligt daar
 * zestig keer boven, en ruim onder de ~180/min per endpoint die de echte lus haalde.
 */
import { reportHandledError } from "./observability";

/** Rollend venster waarover geteld wordt. */
const VENSTER_MS = 60_000;
/** Meer dan dit aantal verzoeken naar hetzelfde endpoint binnen het venster = lus. */
export const DREMPEL_PER_VENSTER = 60;
/** Na een melding zwijgt dezelfde sleutel zo lang. Eén incident, één waarschuwing. */
const AFKOELING_MS = 30 * 60_000;
/** Begrenst het geheugen bij een lange sessie. */
const MAX_SLEUTELS = 200;

/**
 * Endpoints die uit hoofde van hun functie herhaald worden aangeroepen en dus nooit
 * als lus mogen gelden. Bewust een korte, expliciete lijst: elke uitzondering is een
 * blinde vlek, dus die moet je opschrijven en niet afleiden.
 */
const LEGITIEME_HERHALING = [
  "/rest/v1/platform_support_grants",
  "/rest/v1/edge_worker_runs",
  "/functions/v1/platform-health-check",
];

/**
 * Normaliseert een URL tot een stabiele sleutel: geen origin, geen querystring, en
 * id's vervangen. Zonder dat laatste is elk dossier zijn eigen sleutel en telt een
 * lus over verschillende dossiers nooit op.
 */
export function normaliseerEndpoint(url: string): string {
  let pad = url;
  try {
    pad = new URL(url, typeof location !== "undefined" ? location.origin : "http://x").pathname;
  } catch {
    pad = url.split("?")[0] ?? url;
  }
  return pad
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/:id")
    .replace(/\/\d{3,}/g, "/:n");
}

type Teller = { tijden: number[]; zwijgTot: number };

const tellers = new Map<string, Teller>();

/** Alleen voor tests: begint met een schone lei. */
export function resetLoopGuard(): void {
  tellers.clear();
}

export function isLegitiemeHerhaling(sleutel: string): boolean {
  return LEGITIEME_HERHALING.some((l) => sleutel.startsWith(l));
}

/**
 * Registreert één verzoek en geeft terug of dit er één is die gemeld moet worden.
 * Puur en synchroon: geen netwerk, geen opslag, geen timers.
 *
 * Geeft `true` op precies het verzoek dat de drempel overschrijdt, en daarna niet
 * meer tot de afkoeling voorbij is.
 */
export function registreerVerzoek(url: string, nu: number): { sleutel: string; melden: boolean; aantal: number } {
  const sleutel = normaliseerEndpoint(url);
  if (isLegitiemeHerhaling(sleutel)) {
    return { sleutel, melden: false, aantal: 0 };
  }

  let teller = tellers.get(sleutel);
  if (!teller) {
    if (tellers.size >= MAX_SLEUTELS) {
      // Oudste sleutel opruimen; een lus is per definitie recent.
      const eerste = tellers.keys().next().value;
      if (eerste !== undefined) tellers.delete(eerste);
    }
    teller = { tijden: [], zwijgTot: 0 };
    tellers.set(sleutel, teller);
  }

  const grens = nu - VENSTER_MS;
  teller.tijden = teller.tijden.filter((t) => t > grens);
  teller.tijden.push(nu);

  if (teller.tijden.length <= DREMPEL_PER_VENSTER) {
    return { sleutel, melden: false, aantal: teller.tijden.length };
  }
  if (nu < teller.zwijgTot) {
    return { sleutel, melden: false, aantal: teller.tijden.length };
  }
  teller.zwijgTot = nu + AFKOELING_MS;
  return { sleutel, melden: true, aantal: teller.tijden.length };
}

/**
 * Hangt de teller om `window.fetch`. Idempotent: tweemaal aanroepen installeert
 * niet twee wrappers.
 *
 * De guard MELDT ALLEEN en grijpt nooit in: een verzoek tegenhouden zou van een
 * meetinstrument een storingsbron maken.
 */
let geinstalleerd = false;

export function installRequestLoopGuard(): void {
  if (geinstalleerd || typeof window === "undefined" || typeof window.fetch !== "function") return;
  geinstalleerd = true;

  const origineel = window.fetch.bind(window);

  window.fetch = function bewaakteFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    try {
      const url =
        typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
      // Het Sentry-verkeer zelf NOOIT meetellen: anders kan het melden van een lus
      // zichzelf als lus aanmerken.
      if (!/sentry\.io|ingest\./i.test(url)) {
        const uitkomst = registreerVerzoek(url, Date.now());
        if (uitkomst.melden) {
          reportHandledError(
            new Error(`Verzoeklus gedetecteerd: ${uitkomst.aantal} aanroepen binnen 60s naar ${uitkomst.sleutel}`),
            {
              component: "request-loop-guard",
              operation: uitkomst.sleutel,
              code: "request_loop_detected",
            },
          );
        }
      }
    } catch {
      // Meten mag het verzoek nooit breken.
    }
    return origineel(input as RequestInfo, init);
  };
}
